import React, { useEffect, useRef, useMemo, useCallback } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { PerspectiveCamera } from '@react-three/drei'
import * as THREE from 'three'
import particlesVertexShader from './shaders/canvas_particles/vertex.ts'
import particlesFragmentShader from './shaders/canvas_particles/fragment.ts'
import gsap from 'gsap'

const Aboutscene: React.FC = () => {

  return (
    <>
      <Canvas 
        className='aboutcnv'
        gl={{ preserveDrawingBuffer: true }}
        dpr={[1, 2]}
        resize={{ scroll: false }}
      >
        <CameraAndControls />
        <Particles />
        {/* <Center>
          <Sparkles
            size={6}
            scale={[4, 5, 7]}
            position-y={-1}
            speed={0.5}
            count={20}
            color={new THREE.Color(0xAAAAFF)}
            noise={.5}
          />
        </Center> */}
      </Canvas>
    </>
  )
}

const CameraAndControls: React.FC = () => {
    return (
      <>
        <PerspectiveCamera makeDefault position={[0, 0, 18]} fov={35} />
      </>
    )
  }

  
  const Particles: React.FC = () => {
    const particleSize  =  0.19
    const materialRef = useRef<THREE.ShaderMaterial>(null)
    const particlesRef = useRef(null)
    const clockRef = useRef(new THREE.Clock())
    const indexTexture = useRef(0)
    const intervalRef = useRef<number | null>(null)
    const timeoutRef = useRef<number | null>(null)
    const timeout2Ref = useRef<number | null>(null)

    const { size, viewport } = useThree()

    // Mémoriser la géométrie pour éviter de la recréer à chaque rendu
    const geometry = useMemo(() => {
      const geom = new THREE.PlaneGeometry(10, 10, 256, 256)
      geom.deleteAttribute('normal')
      
      const intensitiesArray = new Float32Array(geom.attributes.position.count)
      const anglesArray = new Float32Array(geom.attributes.position.count)

      for(let i = 0; i < geom.attributes.position.count; i++) {
        intensitiesArray[i] = Math.random() / 2
        anglesArray[i] = Math.random() * Math.PI * 2
      }

      geom.setAttribute('aIntensity', new THREE.BufferAttribute(intensitiesArray, 1))
      geom.setAttribute('aAngle', new THREE.BufferAttribute(anglesArray, 1))
      return geom
    }, [])

    // Mémoriser les textures pour éviter de les recréer à chaque rendu
    const textures = useMemo(() => {
      const loader = new THREE.TextureLoader()
      return [
        loader.load('./tolexia_v2-nobg.png'),
        loader.load('./webdeveloper_text.png'),
      ]
    }, [])

    // Utiliser useCallback pour mémoriser la fonction morphTextures
    const morphTextures = useCallback((init: boolean) => {
        if (materialRef.current) 
        {
            if(init) {
                gsap.fromTo(
                    materialRef.current.uniforms.uProgress,
                    { value: 0 },
                    { value: 1, duration: 3, ease: 'linear' }
                )
            }
            else{
                gsap.fromTo(
                    materialRef.current.uniforms.uProgress,
                    { value: 1 },
                    { 
                        value: 0, 
                        duration: 3, 
                        ease: 'linear', 
                        onComplete: () => {
                            if (materialRef.current) {
                                indexTexture.current = (indexTexture.current + 1) % textures.length
                                materialRef.current.uniforms.uPictureTexture.value = textures[indexTexture.current]
                                gsap.fromTo(
                                    materialRef.current.uniforms.uProgress,
                                    { value: 0 },
                                    { value: 1, duration: 3, ease: 'linear' }
                                )
                            }
                        }
                    }
                )
            }
        }
        return indexTexture.current
    }, [textures])

    useEffect(() => {
        // Nettoyer les timers précédents si le composant est remonté
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        if (timeout2Ref.current) clearTimeout(timeout2Ref.current)
        if (intervalRef.current) clearInterval(intervalRef.current)

        timeoutRef.current = window.setTimeout(() => {
            indexTexture.current = morphTextures(true)
            timeout2Ref.current = window.setTimeout(() => {
                morphTextures(false)
                intervalRef.current = window.setInterval(() => {
                    indexTexture.current = morphTextures(false)
                }, 11000)
            }, 5000)
        }, 10)

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            if (timeout2Ref.current) clearTimeout(timeout2Ref.current)
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [morphTextures])

    // Mémoriser les uniforms pour éviter de les recréer
    const uniforms = useMemo(() => ({
        uResolution: { value: new THREE.Vector2() },
        uPictureTexture: { value: textures[0] },
        uParticleSize: { value: particleSize },
        uProgress: { value: 0 },
        uColorA: { value: new THREE.Color('#d66ba4') },
        uColorB: { value: new THREE.Color('#60a4d7') },
        uTime: { value: 0 },
    }), [textures, particleSize])

    useFrame(() => {
      if (materialRef.current && clockRef.current) {
        const elapsedTime = clockRef.current.getElapsedTime()
        materialRef.current.uniforms.uTime.value = elapsedTime
        materialRef.current.uniforms.uResolution.value.set(size.width * viewport.initialDpr, size.height * viewport.initialDpr)
        materialRef.current.uniforms.uParticleSize.value = particleSize
      }
    })
  
    if (!geometry) return null
    return (
      <points geometry={geometry} ref={particlesRef}>
        <shaderMaterial 
          ref={materialRef}
          vertexShader={particlesVertexShader}
          fragmentShader={particlesFragmentShader}
          uniforms={uniforms}
        />
      </points>
    )
  }


export default Aboutscene